/**
 * Engine roster helpers — moved from infra/lambda/audit-trigger/index.ts (lot A step 3).
 *
 * The `PRIMARY_BY_TIER` and selectable-set are now DERIVED from the registry's
 * `engines.allowlistDefaults` so the Trigger Lambda's need computation, the server's
 * engine-allowance check and the Prompt Studio launch planner always agree on which
 * engines a tier may run.
 *
 * Registry-to-run-path mapping: `chatgpt` → `openai`; all other ids are the same.
 *
 * See docs/plans/prompt-studio-build/A-backend-core.md step 3.
 */

import { METRIC_REGISTRY } from './metering';

// ---------------------------------------------------------------------------
// Internal constants (not exported — consumers use the exported functions)
// ---------------------------------------------------------------------------

/** Registry name → run-path name mapping (chatgpt ↔ openai). */
function toRunPath(registryId: string): string {
  return registryId === 'chatgpt' ? 'openai' : registryId;
}

/**
 * The BP-measurable run-path engines for a given tier's DEFAULT (no explicit selection).
 *
 * Per lot A step 3 (C17.1):
 * - basic/pro/elite: the tier's registry allowlist IS the default roster.
 * - enterprise/agency: the four primaries by default (secondaries only on explicit selection).
 *   "enterprise and agency keep today's rule (the four primaries by default, a secondary
 *    engine only when explicitly selected)" — the registry's allowlist for agency/enterprise
 *    defines the SELECTABLE set, not the default.
 */
function registryTierEngines(tier: string): string[] {
  // enterprise + agency: 4 primaries remain the default (today's rule, unchanged by C17.1)
  if (tier === 'enterprise' || tier === 'agency') {
    // chatgpt → openai; the four primaries
    return ['openai', 'gemini', 'claude', 'perplexity'];
  }
  const def = METRIC_REGISTRY.engines;
  const registryList =
    (def.allowlistDefaults as Record<string, string[] | undefined>)[tier] ??
    (def.allowlistDefaults as Record<string, string[] | undefined>)['basic'] ??
    [];
  // 'google' is conversation-only (not a BP engine); drop it from the BP roster.
  return registryList.filter((id) => id !== 'google').map(toRunPath);
}

/**
 * Run-path engine ids that a tier may EVER use for Brand Perception (the full
 * selectable set: primary roster + any secondaries in the tier's allowlist).
 * For basic/pro/elite the selectable set equals the tier's roster (they have no
 * explicit selection UI for BP, so they always run their full list).
 * For enterprise/agency: the full METRIC_REGISTRY.engines.allowlistDefaults list.
 */
function tierSelectableSet(tier: string): string[] {
  // enterprise/agency: everything in the allowlist (secondaries selectable)
  if (tier === 'enterprise' || tier === 'agency') {
    return (
      (METRIC_REGISTRY.engines.allowlistDefaults as Record<string, string[]>)?.[tier] ??
      []
    )
      .filter((id) => id !== 'google')
      .map(toRunPath);
  }
  // basic/pro/elite: selectable = their roster (no separate selection)
  return registryTierEngines(tier);
}

// ---------------------------------------------------------------------------
// Per-axis full roster (the set of BP engines that can ever run on this axis)
// ---------------------------------------------------------------------------

/**
 * Full BP-engine roster per audit-type axis.
 * `brand_comparison` shares the `brand_narrative` roster (C1: ENGINES_BY_AXIS.comparison = narrative roster).
 */
const AXIS_ROSTER: Record<string, readonly string[]> = {
  brand_narrative: ['perplexity', 'openai', 'gemini', 'claude', 'mistral', 'ai_overview', 'google_ai_mode'],
  brand_authority: ['perplexity', 'openai', 'gemini', 'claude', 'mistral', 'ai_overview', 'google_ai_mode'],
  brand_referral:  ['perplexity', 'openai', 'gemini', 'claude', 'mistral', 'ai_overview', 'google_ai_mode'],
  brand_comparison: ['perplexity', 'openai', 'gemini', 'claude', 'mistral', 'ai_overview', 'google_ai_mode'],
} as const;

// ---------------------------------------------------------------------------
// Exported helpers
// ---------------------------------------------------------------------------

/**
 * The run-path engine ids the brand may use for a given axis, intersected
 * with the tier's selectable set + an explicit selection when provided.
 *
 * Behaviour:
 *   - enterprise → reads tenant-wide `tenantEnabledEngines`
 *   - agency     → reads per-brand `brandEnginesEnabled`
 *   - basic/pro/elite → no explicit selection (run the full tier roster)
 *
 * EXPLICIT-ONLY rule: a secondary engine runs only when its id is explicitly
 * selected. An explicit selection runs EXACTLY what is chosen (intersected with
 * the tier's selectable set). EMPTY/UNSET → the tier's primary roster only.
 */
export function allowedEnginesForAxis(
  axis: string,
  tier: string,
  brandEnginesEnabled?: string[],
  tenantEnabledEngines?: string[],
): string[] {
  const roster = AXIS_ROSTER[axis];
  if (!roster) return [];

  const primaryRoster = registryTierEngines(tier);
  const selectable = tierSelectableSet(tier);

  // enterprise reads the tenant-wide list; everyone else the per-brand list.
  const selection = tier === 'enterprise' ? tenantEnabledEngines : brandEnginesEnabled;

  let allowed: string[];
  if (Array.isArray(selection) && selection.length > 0) {
    const sel = new Set(selection.map((e) => String(e || '').trim().toLowerCase()));
    const chosen = selectable.filter((e) => sel.has(e));
    allowed = chosen.length > 0 ? chosen : primaryRoster;
  } else {
    allowed = primaryRoster;
  }

  const allowedSet = new Set(allowed);
  return roster.filter((e) => allowedSet.has(e));
}

/**
 * Every engine a tier may select for this axis, in roster order, whatever is selected now
 * (the Prompt Studio engine tiles, UI-SPEC §2.24: the active ones are `allowedEnginesForAxis`).
 */
export function selectableEnginesForAxis(axis: string, tier: string): string[] {
  const selectable = new Set(tierSelectableSet(tier));
  return (AXIS_ROSTER[axis] ?? []).filter((e) => selectable.has(e));
}

/**
 * The number of engines that will run for this axis / tier / selection.
 * Used by the Trigger Lambda's credit estimate (00b DQ14 is lot G's Trigger update;
 * this feeds today's Trigger and the Prompt Studio's need computation).
 */
export function allowedEngineCountForAxis(
  axis: string,
  tier: string,
  brandEnginesEnabled?: string[],
  tenantEnabledEngines?: string[],
): number {
  return allowedEnginesForAxis(axis, tier, brandEnginesEnabled, tenantEnabledEngines).length;
}

/**
 * Resolve the full set of allowed BP engines (not per-axis).
 * Kept for backward compat with code that does not know the axis yet;
 * prefer `allowedEnginesForAxis` when the axis is known.
 */
export function resolveAllowedEngines(
  tier: string,
  brandEnginesEnabled?: string[],
  tenantEnabledEngines?: string[],
): string[] {
  return allowedEnginesForAxis('brand_narrative', tier, brandEnginesEnabled, tenantEnabledEngines);
}
